import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { examApi, type ReportContent } from '../../services/examApi';
import { QuestionDetailModal } from '../../components/exam/QuestionDetailModal';

interface QTarget { subject: string; moduleNo: number; sort: number; }

/**
 * 轻量 markdown 渲染：仅覆盖报告生成器产出的语法子集（# / ## 标题、- 列表、| 表格）。
 * 表格首列 "#"（题号）可点击，回调打开题目详情。模块上下文由 "## <Subject> · Module <N>" 头解析。
 */
const MarkdownReport: React.FC<{ markdown: string; onPick: (t: QTarget) => void }> = ({ markdown, onPick }) => {
  const lines = markdown.split('\n');
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;
  let ctx: { subject: string; moduleNo: number } | null = null;

  const renderInline = (text: string) =>
    text.split(/(\*\*[^*]+\*\*)/g).map((seg, idx) =>
      seg.startsWith('**') && seg.endsWith('**') ? (
        <strong key={idx}>{seg.slice(2, -2)}</strong>
      ) : (
        <React.Fragment key={idx}>{seg}</React.Fragment>
      )
    );

  while (i < lines.length) {
    const line = lines[i];

    // 红色提示 span（报告生成器输出的内联颜色提示）
    const colorSpan = line.match(/^<span style="color:([^"]+)">([\s\S]*?)<\/span>$/);
    if (colorSpan) {
      blocks.push(
        <p key={key++} className="text-sm mb-3" style={{ color: colorSpan[1] }}>
          {colorSpan[2]}
        </p>
      );
      i++;
      continue;
    }

    if (line.startsWith('## ')) {
      const heading = line.slice(3);
      // 解析 "<Subject> · Module <N>"
      const m = heading.match(/^(.+?)\s*·\s*Module\s+(\d+)/i);
      if (m) ctx = { subject: m[1].trim().toLowerCase(), moduleNo: Number(m[2]) };
      blocks.push(<h2 key={key++} className="text-lg font-semibold text-gray-800 mt-6 mb-2">{heading}</h2>);
      i++;
      continue;
    }
    if (line.startsWith('# ')) {
      blocks.push(<h1 key={key++} className="text-2xl font-bold text-gray-900 mb-3">{line.slice(2)}</h1>);
      i++;
      continue;
    }
    if (line.startsWith('- ')) {
      const items: string[] = [];
      while (i < lines.length && lines[i].startsWith('- ')) { items.push(lines[i].slice(2)); i++; }
      blocks.push(
        <ul key={key++} className="list-disc pl-5 space-y-1 text-gray-700 mb-3">
          {items.map((it, idx) => <li key={idx}>{renderInline(it)}</li>)}
        </ul>
      );
      continue;
    }
    // 表格
    if (line.startsWith('|') && i + 1 < lines.length && /^\|[\s:|-]+\|$/.test(lines[i + 1])) {
      const parseRow = (row: string) =>
        row.slice(1, row.endsWith('|') ? -1 : undefined).split('|').map((c) => c.trim());
      const header = parseRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].startsWith('|')) { rows.push(parseRow(lines[i])); i++; }
      const rowCtx = ctx; // 固定当前模块上下文
      blocks.push(
        <div key={key++} className="overflow-x-auto mb-4">
          <table className="min-w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-gray-200">
                {header.map((h, idx) => (
                  <th key={idx} className="text-left py-2 px-3 font-medium text-gray-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => {
                const sort = Number(r[0]);
                const clickable = rowCtx && Number.isFinite(sort);
                return (
                  <tr key={ri} className="border-b border-gray-100">
                    {r.map((c, ci) => {
                      if (ci === 0 && clickable) {
                        return (
                          <td key={ci} className="py-1.5 px-3">
                            <button
                              onClick={() => onPick({ subject: rowCtx!.subject, moduleNo: rowCtx!.moduleNo, sort })}
                              className="text-primary-600 hover:text-primary-800 font-medium underline underline-offset-2"
                              title="View question"
                            >
                              {c}
                            </button>
                          </td>
                        );
                      }
                      return <td key={ci} className="py-1.5 px-3 text-gray-700">{c}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
      continue;
    }
    if (line.trim() === '') { i++; continue; }
    blocks.push(<p key={key++} className="text-gray-700 mb-2">{renderInline(line)}</p>);
    i++;
  }

  return <div>{blocks}</div>;
};

const ReportPage: React.FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<ReportContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<QTarget | null>(null);

  useEffect(() => {
    if (!attemptId) return;
    let mounted = true;
    setLoading(true);
    examApi
      .getReport(attemptId)
      .then((res) => { if (mounted) { setReport(res); setLoading(false); } })
      .catch((e: any) => { if (mounted) { setError(e?.message || 'Failed to load report'); setLoading(false); } });
    return () => { mounted = false; };
  }, [attemptId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading report…</div>;
  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-red-500">{error}</p>
      <button className="btn btn-primary" onClick={() => navigate('/')}>Back to list</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-primary-50/30">
      <header className="h-14 flex items-center justify-between px-6 bg-white border-b border-primary-100">
        <h2 className="font-semibold text-gray-800">Exam Report</h2>
        <button onClick={() => navigate('/')} className="text-sm text-gray-500 hover:text-gray-700">← Back to list</button>
      </header>
      <main className="max-w-3xl mx-auto p-6">
        <div className="card p-8">
          {report && <MarkdownReport markdown={report.markdown} onPick={setPicked} />}
        </div>
      </main>

      {report && picked && (
        <QuestionDetailModal
          examId={report.examId}
          target={picked}
          answers={report.answers}
          questions={report.questions}
          onClose={() => setPicked(null)}
        />
      )}
    </div>
  );
};

export default ReportPage;
